import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { PhysicsBody } from "../../core/physics/PhysicsBody";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { startPointerDrag } from "../../utils/pointerDrag";
import { styleTxn } from "../../utils/style-txn";

type MarqueeDirection = "left" | "right" | "up" | "down";

type MarqueeState = {
  host: HTMLElement;
  track: HTMLDivElement;
  part: HTMLElement;
  parts: HTMLElement[];
  clones: HTMLElement[];
  direction: MarqueeDirection;
  speed: number;
  gap: number;
  fill: boolean;
  pauseOnHover: boolean;
  progressEnabled: boolean;
  progressWasEnabled: boolean;
  hovering: boolean;
  position: number;
  hostSize: number;
  partSize: number;
  cycleWidth: number;
  initialOffset: number;
  lastAppliedTranslate: number;
  partProgressApplied: number[];
  partClass: string;
  partClassApplied: string;
  appliedLayoutKey: string;
  refreshScheduled: boolean;
  refreshQueued: boolean;
  visibleProgressStart: number;
  visibleProgressEnd: number;
  resizeObserver: ResizeObserver | null;
  lastObservedHostSize: number;
  lastObservedPartSize: number;
  draggable: boolean;
  dragFriction: number;
  dragSensitivity: number;
  isDragging: boolean;
  dragBody: PhysicsBody;
  dragDisposer: (() => void) | null;
  // Sticky autoplay sign: follows the direction of the last fling until the
  // next drag, independent of the configured `direction` attribute (which
  // still governs orientation/layout, not which way autoplay resumes).
  autoplaySign: 1 | -1;
  // A drag gesture that actually moved the track still fires a native "click"
  // on pointerup — set once a drag exceeds the noise threshold, consumed
  // (and cleared) by the very next click so it doesn't get mistaken for an
  // item interaction.
  suppressNextClick: boolean;
  // `suppressNextClick` is armed on pointerup and disarmed by the click that
  // follows it — but that click is not guaranteed to arrive (touch browsers
  // drop it when the gesture ends as a scroll/cancel), and a stuck flag would
  // then eat the next legitimate tap. Stamped so it can expire on its own.
  suppressNextClickAt: number;
  // The node actually under the pointer at press time. On touch there is no
  // hover, so `pause-on-hover` never engages and the track keeps scrolling
  // between pointerdown and click — a hit-test at click time would resolve a
  // different item than the one the user pressed.
  pointerDownTarget: HTMLElement | null;
  // External hold, distinct from pauseOnHover — e.g. a caller expanding one of
  // this marquee's items into a lightbox, where the item flying off mid-loop
  // while it's open would make the close animation land somewhere nonsensical.
  paused: boolean;
};

type MarqueeMeasurement = {
  isVert: boolean;
  hostSize: number;
  partSize: number;
  cycleWidth: number;
  initialOffset: number;
  totalCopies: number;
};

const states = new WeakMap<StringObject, MarqueeState>();
const DEFAULT_SPEED = 60;
// Below this, a ResizeObserver firing is sub-pixel noise (scroll-linked
// transforms/elastic containers, font hinting), not a real layout change —
// refreshing on every such tick forces a reflow per frame for no reason.
const RESIZE_NOISE_EPSILON_PX = 0.5;
const DEFAULT_DRAG_FRICTION = 0.08;
const DEFAULT_DRAG_SENSITIVITY = 1;
// PhysicsBody.isAtRest() also checks `value` near zero, which fits
// spring-restoring bodies (StringImpulse) — our drag body is a plain
// friction-only flywheel whose `value` accumulates without bound, so it
// never satisfies that check. Sleep is decided on velocity alone instead.
const DRAG_SLEEP_VELOCITY_PX_S = 1;
// Displacement from the press point past which a gesture counts as a drag and
// its trailing click is swallowed. A mouse stays put between down and up, so a
// tight threshold is safe; a finger never does — normal taps wander several
// pixels — hence the far looser touch/pen slop, in line with the platform
// tap-vs-scroll thresholds.
const DRAG_CLICK_SUPPRESS_THRESHOLD_PX = 4;
const TOUCH_DRAG_CLICK_SUPPRESS_THRESHOLD_PX = 12;
// A real click follows its pointerup within a frame or two; anything later is
// a separate interaction and must not be suppressed.
const CLICK_SUPPRESS_TTL_MS = 500;

const normalizeLoopPosition = (value: number, cycleWidth: number) => {
  if (!(cycleWidth > 0)) return 0;
  if (value >= cycleWidth) return value % cycleWidth;
  if (value < 0) return ((value % cycleWidth) + cycleWidth) % cycleWidth;
  return value;
};

const clampSpeed = (value: number | null) => {
  if (!Number.isFinite(value)) return DEFAULT_SPEED;
  return Math.max(0, Math.abs(value as number));
};

const isVerticalDirection = (dir: MarqueeDirection): boolean => {
  return dir === "up" || dir === "down";
};

export class StringMarquee extends StringModule {
  private onFontsReadyBound: () => void;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "marquee";

    this.cssProperties = [
      { name: "--marquee-progress", syntax: "<number>", initialValue: "0", inherits: false },
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "marquee-direction",
        type: { type: "enum", values: ["left", "right", "up", "down"] },
        fallback: this.settings["marquee-direction"] ?? "left",
      },
      {
        key: "marquee-speed",
        type: "number",
        fallback: this.settings["marquee-speed"] ?? DEFAULT_SPEED,
      },
      {
        key: "marquee-gap",
        type: "dimension",
        fallback: this.settings["marquee-gap"] ?? 0,
      },
      {
        key: "marquee-fill",
        type: "boolean",
        fallback: this.settings["marquee-fill"] ?? false,
      },
      {
        key: "marquee-pause-on-hover",
        type: "boolean",
        fallback: this.settings["marquee-pause-on-hover"] ?? false,
      },
      {
        key: "marquee-progress",
        type: "boolean",
        fallback: this.settings["marquee-progress"] ?? false,
      },
      {
        key: "marquee-part-class",
        type: "string",
        fallback: this.settings["marquee-part-class"] ?? "",
      },
      {
        key: "marquee-draggable",
        type: "boolean",
        fallback: this.settings["marquee-draggable"] ?? false,
      },
      {
        key: "marquee-drag-friction",
        type: "number",
        fallback: this.settings["marquee-drag-friction"] ?? DEFAULT_DRAG_FRICTION,
      },
      {
        key: "marquee-drag-sensitivity",
        type: "number",
        fallback: this.settings["marquee-drag-sensitivity"] ?? DEFAULT_DRAG_SENSITIVITY,
      },
    ];

    this.onFontsReadyBound = this.onFontsReady.bind(this);
  }

  override onInit(): void {
    if ("fonts" in document && document.fonts) {
      document.fonts.ready.then(this.onFontsReadyBound);
      document.fonts.addEventListener?.("loadingdone", this.onFontsReadyBound);
    }
    this.events.on<{ host: HTMLElement; paused: boolean }>(
      "marquee:set-paused",
      this.onSetPaused,
    );
  }

  override onUnsubscribe(): void {
    if ("fonts" in document && document.fonts) {
      document.fonts.removeEventListener?.("loadingdone", this.onFontsReadyBound);
    }
    this.events.off("marquee:set-paused", this.onSetPaused);
  }

  private onSetPaused = (data: { host: HTMLElement; paused: boolean }): void => {
    const state = this.findStateByHost(data.host);
    if (state) state.paused = data.paused;
  };

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);

    let state = states.get(object);
    if (!state) {
      state = this.createState(object.htmlElement);
      states.set(object, state);
      this.mountStructure(state);
    }

    this.syncConfig(object, state);
    this.refresh(state);
  }

  override onObjectDisconnected(object: StringObject): void {
    const state = states.get(object);
    if (!state) return;

    state.host.removeEventListener("mouseenter", this.onMouseEnter);
    state.host.removeEventListener("mouseleave", this.onMouseLeave);
    state.host.removeEventListener("pointerdown", this.onPointerDown);
    state.host.removeEventListener("dragstart", this.onDragStart);
    state.host.removeEventListener("click", this.onHostClick);
    state.dragDisposer?.();
    state.resizeObserver?.disconnect();
  }

  override onResizeWidth(): void {
    this.refreshAll();
  }

  // Ignores which nodes changed; just checks each instance's structure is intact.
  override onDOMMutate(_added: NodeList, _removed: NodeList): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (!state) continue;
      if (state.track.parentNode === state.host) continue;
      this.recoverStructure(state);
    }
  }

  private refreshAll(): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (!state) continue;
      this.syncConfig(this.objectsOnPage[i], state);
      this.refresh(state);
    }
  }

  override onFrame(data: StringData): void {
    const dt = Math.max(1 / 240, Math.min(0.1, (data.time.delta || 16.6667) / 1000));

    styleTxn.run(() => {
      for (let i = 0; i < this.objectsOnPage.length; i++) {
        const state = states.get(this.objectsOnPage[i]);
        if (!state || !(state.cycleWidth > 0)) continue;

        if (state.isDragging) {
          // Position is driven directly by the pointermove handler while a
          // drag is in progress — autoplay and inertia both stay silent.
        } else if (!state.paused && Math.abs(state.dragBody.velocity) > DRAG_SLEEP_VELOCITY_PX_S) {
          const before = state.dragBody.value;
          state.dragBody.step(dt);
          const delta = state.dragBody.value - before;
          state.position = normalizeLoopPosition(state.position + delta, state.cycleWidth);
          if (Math.abs(state.dragBody.velocity) <= DRAG_SLEEP_VELOCITY_PX_S) {
            state.dragBody.reset();
          }
        } else if (!state.paused && !(state.pauseOnHover && state.hovering) && state.speed > 0) {
          state.position = normalizeLoopPosition(
            state.position + state.autoplaySign * state.speed * dt,
            state.cycleWidth,
          );
        }

        const isInv = state.direction === "right" || state.direction === "down";
        const translate =
          (isInv ? state.position - state.cycleWidth : -state.position) - state.initialOffset;

        if (translate === state.lastAppliedTranslate) continue;
        state.lastAppliedTranslate = translate;

        const transformStr = isVerticalDirection(state.direction)
          ? `translate3d(0, ${translate}px, 0)`
          : `translate3d(${translate}px, 0, 0)`;

        styleTxn.setProp(state.track, "transform", transformStr);
        this.updatePartProgress(state, translate);
      }
    });
  }

  private createState(host: HTMLElement): MarqueeState {
    const track = document.createElement("div");
    const part = document.createElement("span");

    track.setAttribute("data-string-marquee-track", "");
    part.setAttribute("data-string-marquee-part", "");

    host.addEventListener("mouseenter", this.onMouseEnter);
    host.addEventListener("mouseleave", this.onMouseLeave);
    host.addEventListener("pointerdown", this.onPointerDown);
    host.addEventListener("dragstart", this.onDragStart);
    host.addEventListener("click", this.onHostClick);

    const state: MarqueeState = {
      host,
      track,
      part,
      parts: [part],
      clones: [],
      direction: "left",
      speed: DEFAULT_SPEED,
      gap: 0,
      fill: false,
      pauseOnHover: false,
      progressEnabled: false,
      progressWasEnabled: false,
      hovering: false,
      position: 0,
      hostSize: 0,
      partSize: 0,
      cycleWidth: 0,
      initialOffset: 0,
      lastAppliedTranslate: Number.NaN,
      partProgressApplied: [Number.NaN],
      partClass: "",
      partClassApplied: "",
      appliedLayoutKey: "",
      refreshScheduled: false,
      refreshQueued: false,
      visibleProgressStart: -1,
      visibleProgressEnd: -1,
      resizeObserver: null,
      lastObservedHostSize: -1,
      lastObservedPartSize: -1,
      draggable: false,
      dragFriction: DEFAULT_DRAG_FRICTION,
      dragSensitivity: DEFAULT_DRAG_SENSITIVITY,
      isDragging: false,
      dragBody: new PhysicsBody({ friction: DEFAULT_DRAG_FRICTION }),
      dragDisposer: null,
      autoplaySign: 1,
      suppressNextClick: false,
      suppressNextClickAt: 0,
      pointerDownTarget: null,
      paused: false,
    };

    if (typeof ResizeObserver !== "undefined") {
      state.resizeObserver = new ResizeObserver((entries) => {
        let changed = false;
        for (let i = 0; i < entries.length; i++) {
          const rect = entries[i].contentRect;
          const size = rect.width + rect.height;
          const isHost = entries[i].target === state.host;
          const last = isHost ? state.lastObservedHostSize : state.lastObservedPartSize;
          if (last < 0 || Math.abs(size - last) > RESIZE_NOISE_EPSILON_PX) {
            changed = true;
          }
          if (isHost) {
            state.lastObservedHostSize = size;
          } else {
            state.lastObservedPartSize = size;
          }
        }
        if (changed) this.refresh(state);
      });
      state.resizeObserver.observe(host);
      state.resizeObserver.observe(part);
    }

    return state;
  }

  private recoverStructure(state: MarqueeState): void {
    for (let i = 0; i < state.clones.length; i++) {
      state.clones[i].remove();
    }
    state.clones.length = 0;
    state.parts = [state.part];
    state.partProgressApplied = [Number.NaN];

    this.mountStructure(state);
    this.refresh(state);
  }

  private mountStructure(state: MarqueeState): void {
    const { host, track, part } = state;

    while (part.firstChild) {
      part.removeChild(part.firstChild);
    }
    while (host.firstChild) {
      part.appendChild(host.firstChild);
    }

    track.appendChild(part);
    host.appendChild(track);

    styleTxn.run(() => {
      styleTxn.setProps(host, {
        overflow: "hidden",
      });
      styleTxn.setProps(track, {
        display: "flex",
        willChange: "transform",
      });
      styleTxn.setProps(part, {
        flex: "0 0 auto",
      });
    });
  }

  private syncConfig(object: StringObject, state: MarqueeState): void {
    state.direction = object.getProperty<MarqueeDirection>("marquee-direction") ?? "left";
    state.speed = clampSpeed(object.getProperty<number>("marquee-speed"));
    state.gap = Math.max(0, object.getProperty<number>("marquee-gap") ?? 0);
    state.fill = object.getProperty<boolean>("marquee-fill") === true;
    state.pauseOnHover = object.getProperty<boolean>("marquee-pause-on-hover") === true;
    state.progressEnabled = object.getProperty<boolean>("marquee-progress") === true;
    state.partClass = object.getProperty<string>("marquee-part-class") ?? "";
    state.draggable = object.getProperty<boolean>("marquee-draggable") === true;
    state.dragSensitivity =
      object.getProperty<number>("marquee-drag-sensitivity") ?? DEFAULT_DRAG_SENSITIVITY;
    const dragFriction =
      object.getProperty<number>("marquee-drag-friction") ?? DEFAULT_DRAG_FRICTION;
    state.dragFriction = Math.max(0, Math.min(1, dragFriction));
  }

  private refresh(state: MarqueeState): void {
    this.applyStaticLayout(state);
    this.scheduleRefresh(state);
  }

  private applyStaticLayout(state: MarqueeState): void {
    this.syncPartClass(state);
    const isVert = isVerticalDirection(state.direction);
    const layoutKey = `${state.direction}|${state.gap}|${state.draggable}`;
    if (state.appliedLayoutKey === layoutKey) return;

    state.appliedLayoutKey = layoutKey;

    styleTxn.run(() => {
      styleTxn.setProps(state.track, {
        flexDirection: isVert ? "column" : "row",
        flexWrap: "nowrap",
        width: isVert ? "100%" : "max-content",
        height: isVert ? "max-content" : "100%",
        rowGap: isVert ? `${state.gap}px` : "0px",
        columnGap: isVert ? "0px" : `${state.gap}px`,
      });
      styleTxn.setProps(state.host, {
        // Let the orthogonal scroll gesture pass through to the page's own
        // scroll handling untouched; only the drag axis is captured via
        // pointer capture in onPointerDown.
        touchAction: state.draggable ? (isVert ? "pan-x" : "pan-y") : "",
        cursor: state.draggable ? "grab" : "",
        // Without this, dragging over <img>/<a> content starts the browser's
        // native drag-and-drop (ghost image, "not-allowed" cursor) instead
        // of our pointer-based drag — blocked outright in onDragStart below,
        // this just also stops text selection from fighting the gesture.
        userSelect: state.draggable ? "none" : "",
      });
    });
  }

  private measure(state: MarqueeState): MarqueeMeasurement {
    const isVert = isVerticalDirection(state.direction);
    const hostRect = state.host.getBoundingClientRect();
    const partRect = state.part.getBoundingClientRect();
    const computedHost = getComputedStyle(state.host);

    const hostSize = isVert ? hostRect.height : hostRect.width;
    const partSize = isVert ? partRect.height : partRect.width;
    const cycleWidth = partSize > 0 ? partSize + state.gap : 0;

    let totalCopies = 1;
    if (hostSize > 0 && cycleWidth > 0) {
      const minCopies = Math.max(2, Math.ceil(hostSize / cycleWidth) + 2);
      totalCopies = state.fill ? minCopies : isVert ? minCopies : 2;
    }

    return {
      isVert,
      hostSize,
      partSize,
      cycleWidth,
      initialOffset: isVert
        ? parseFloat(computedHost.paddingTop)
        : parseFloat(computedHost.paddingLeft),
      totalCopies,
    };
  }

  private applyMeasurement(state: MarqueeState, measurement: MarqueeMeasurement): void {
    this.syncCopies(state, measurement.totalCopies);

    state.hostSize = measurement.hostSize;
    state.partSize = measurement.partSize;
    state.cycleWidth = measurement.cycleWidth;
    state.initialOffset = measurement.initialOffset;

    state.position = normalizeLoopPosition(state.position, state.cycleWidth);
    state.lastAppliedTranslate = Number.NaN;

    for (let i = 0; i < state.partProgressApplied.length; i++) {
      state.partProgressApplied[i] = Number.NaN;
    }
    state.visibleProgressStart = -1;
    state.visibleProgressEnd = -1;

    styleTxn.run(() => {
      const isInv = state.direction === "right" || state.direction === "down";
      const resetVal =
        (isInv ? state.position - state.cycleWidth : -state.position) - state.initialOffset;
      const resetTransform = measurement.isVert
        ? `translate3d(0, ${resetVal}px, 0)`
        : `translate3d(${resetVal}px, 0, 0)`;
      styleTxn.setProp(state.track, "transform", resetTransform);
      if (!state.progressEnabled && state.progressWasEnabled) {
        for (let i = 0; i < state.parts.length; i++) {
          styleTxn.setVar(state.parts[i], "--marquee-progress", 0);
        }
      }
    });

    state.progressWasEnabled = state.progressEnabled;
  }

  private scheduleRefresh(state: MarqueeState): void {
    if (state.refreshScheduled) {
      state.refreshQueued = true;
      return;
    }

    state.refreshScheduled = true;

    frameDOM.measure(() => {
      const measurement = this.measure(state);
      frameDOM.mutate(() => {
        state.refreshScheduled = false;
        this.applyMeasurement(state, measurement);

        if (state.refreshQueued) {
          state.refreshQueued = false;
          this.scheduleRefresh(state);
        }
      });
    });
  }

  private syncCopies(state: MarqueeState, totalCopies: number): void {
    if (state.parts.length === totalCopies) {
      if (state.partProgressApplied.length !== state.parts.length) {
        state.partProgressApplied = new Array(state.parts.length).fill(Number.NaN);
      }
      return;
    }

    for (let i = 0; i < state.clones.length; i++) {
      state.clones[i].remove();
    }
    state.clones.length = 0;
    const fragment = document.createDocumentFragment();

    for (let i = 1; i < totalCopies; i++) {
      const clone = state.part.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-string-marquee-part");
      clone.setAttribute("aria-hidden", "true");
      fragment.appendChild(clone);
      state.clones.push(clone);
    }

    state.track.appendChild(fragment);
    state.parts = [state.part, ...state.clones];
    state.partProgressApplied = new Array(state.parts.length).fill(Number.NaN);
  }

  private syncPartClass(state: MarqueeState): void {
    if (state.partClassApplied === state.partClass) return;
    state.partClassApplied = state.partClass;
    state.part.className = state.partClass;
  }

  private updatePartProgress(state: MarqueeState, translate: number): void {
    if (!state.progressEnabled || !(state.hostSize > 0) || !(state.partSize > 0)) {
      return;
    }

    const denominator = state.hostSize + state.partSize;
    if (!(denominator > 0)) return;

    const isInv = state.direction === "right" || state.direction === "down";
    const visibleStart = -state.partSize;
    const visibleEnd = state.hostSize;
    const minCoordinate = visibleStart - state.partSize;
    const maxCoordinate = visibleEnd;
    const maxIndex = state.parts.length - 1;
    const rangeStart = Math.max(0, Math.floor((minCoordinate - translate) / state.cycleWidth) + 1);
    const rangeEnd = Math.min(
      maxIndex,
      Math.ceil((maxCoordinate - translate) / state.cycleWidth) - 1,
    );

    if (state.visibleProgressStart !== -1 && state.visibleProgressEnd !== -1) {
      for (
        let i = state.visibleProgressStart;
        i < rangeStart && i <= state.visibleProgressEnd;
        i++
      ) {
        state.partProgressApplied[i] = Number.NaN;
      }
      for (
        let i = Math.max(rangeEnd + 1, state.visibleProgressStart);
        i <= state.visibleProgressEnd;
        i++
      ) {
        state.partProgressApplied[i] = Number.NaN;
      }
    }

    state.visibleProgressStart = rangeStart <= rangeEnd ? rangeStart : -1;
    state.visibleProgressEnd = rangeStart <= rangeEnd ? rangeEnd : -1;

    if (rangeStart > rangeEnd) {
      return;
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      const coordinate = translate + i * state.cycleWidth;
      const partStart = coordinate;
      const partEnd = coordinate + state.partSize;
      const visible = partEnd > visibleStart && partStart < visibleEnd;

      if (!visible) {
        if (!Number.isNaN(state.partProgressApplied[i])) {
          state.partProgressApplied[i] = Number.NaN;
        }
        continue;
      }

      const raw = (coordinate + state.partSize) / denominator;
      const clamped = raw <= 0 ? 0 : raw >= 1 ? 1 : raw;
      const progress = isInv ? clamped : 1 - clamped;

      if (state.partProgressApplied[i] === progress) {
        continue;
      }

      state.partProgressApplied[i] = progress;
      styleTxn.setVar(state.parts[i], "--marquee-progress", progress);
    }
  }

  private onFontsReady(): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (!state) continue;
      this.refresh(state);
    }
  }

  private onMouseEnter = (event: Event) => {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) return;

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (state?.host === host) {
        state.hovering = true;
        return;
      }
    }
  };

  private onMouseLeave = (event: Event) => {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) return;

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (state?.host === host) {
        state.hovering = false;
        return;
      }
    }
  };

  private findStateByHost(host: EventTarget | null): MarqueeState | null {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (state?.host === host) return state;
    }
    return null;
  }

  private onDragStart = (event: DragEvent) => {
    const state = this.findStateByHost(event.currentTarget);
    if (state?.draggable) event.preventDefault();
  };

  // Looped copies of `part` are raw cloneNode(true) duplicates (see syncCopies)
  // made for the seamless scroll illusion — they carry no Vue/JS listeners of
  // their own. Delegating the click here, at the stable host, is what makes a
  // click on *any* copy (original or clone) resolve the same way: attributes
  // (including data-* keys authored on the source content) survive cloning
  // even though addEventListener-bound handlers don't.
  private onHostClick = (event: MouseEvent): void => {
    const state = this.findStateByHost(event.currentTarget);
    if (!state) return;

    if (state.suppressNextClick) {
      state.suppressNextClick = false;
      const stale = performance.now() - state.suppressNextClickAt > CLICK_SUPPRESS_TTL_MS;
      if (!stale) {
        state.pointerDownTarget = null;
        return;
      }
    }

    // The node pressed on is the one the user meant, and on touch it is the
    // only trustworthy answer: with no hover to pause it, the track has moved
    // on by click time and a hit-test would land on a different copy. Falls
    // back to a hit-test for clicks with no preceding drag gesture (keyboard
    // activation, non-draggable marquees) — when draggable, onPointerDown
    // captures the pointer on `host` (see startPointerDrag's use of
    // currentTarget), which retargets the click's own `event.target` to `host`.
    const pressed = state.pointerDownTarget;
    state.pointerDownTarget = null;

    const target =
      (pressed && state.host.contains(pressed) ? pressed : null) ??
      (document.elementFromPoint(event.clientX, event.clientY) as HTMLElement) ??
      (event.target as HTMLElement);

    this.events.emit("marquee:click", {
      host: state.host,
      target,
    });
  };

  private onPointerDown = (event: PointerEvent) => {
    if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;

    const state = this.findStateByHost(event.currentTarget);
    if (!state) return;

    // Recorded before the draggable bail-out: onHostClick wants the pressed
    // node regardless of whether a drag gesture follows.
    state.pointerDownTarget = event.target as HTMLElement | null;

    if (!state.draggable || !(state.cycleWidth > 0)) return;

    const isVert = isVerticalDirection(state.direction);
    const isInv = state.direction === "right" || state.direction === "down";

    state.isDragging = true;
    state.dragBody.velocity = 0;
    styleTxn.run(() => styleTxn.setProp(state.host, "cursor", "grabbing"));

    let lastAxisPos = isVert ? event.clientY : event.clientX;
    const startX = event.clientX;
    const startY = event.clientY;
    // Furthest the pointer ever got from where it was pressed. Summing the
    // per-move deltas instead would count a finger's tremor during a stationary
    // tap as movement and wrongly classify it as a drag.
    let maxDisplacement = 0;
    const clickSuppressThreshold =
      event.pointerType === "mouse"
        ? DRAG_CLICK_SUPPRESS_THRESHOLD_PX
        : TOUCH_DRAG_CLICK_SUPPRESS_THRESHOLD_PX;

    // Windowed history of raw pointer-axis positions (unwrapped, unlike
    // state.position which wraps via normalizeLoopPosition) for computing
    // release velocity. The very last pointermove before release is often
    // noisy — a natural deceleration or micro-bounce right before lift-off —
    // and using it alone as "the" fling velocity flips sign unpredictably.
    // Averaging over a short trailing window is what most momentum
    // implementations (UIScrollView, Framer Motion) do instead.
    const VELOCITY_WINDOW_MS = 100;
    const history: { t: number; axisPos: number }[] = [
      { t: performance.now(), axisPos: lastAxisPos },
    ];

    const dispose = startPointerDrag(event, {
      onMove: (moveEvent) => {
        const axisPos = isVert ? moveEvent.clientY : moveEvent.clientX;
        const now = performance.now();
        const rawDelta = axisPos - lastAxisPos;
        lastAxisPos = axisPos;

        const displacement = Math.hypot(
          moveEvent.clientX - startX,
          moveEvent.clientY - startY,
        );
        if (displacement > maxDisplacement) maxDisplacement = displacement;

        const posDelta = (isInv ? rawDelta : -rawDelta) * state.dragSensitivity;
        state.position = normalizeLoopPosition(state.position + posDelta, state.cycleWidth);

        history.push({ t: now, axisPos });
        while (history.length > 1 && now - history[0].t > VELOCITY_WINDOW_MS) {
          history.shift();
        }
      },
      onEnd: () => {
        state.isDragging = false;
        state.dragDisposer = null;
        state.dragBody.friction = state.dragFriction;
        const wasDrag = maxDisplacement > clickSuppressThreshold;
        if (wasDrag) {
          state.suppressNextClick = true;
          state.suppressNextClickAt = performance.now();
        } else {
          // A tap, not a fling: the couple of jittery pixels a finger leaves
          // behind would otherwise be read as a velocity and flip autoplaySign,
          // reversing the whole marquee every time someone taps an item.
          styleTxn.run(() => styleTxn.setProp(state.host, "cursor", "grab"));
          return;
        }

        const oldest = history[0];
        const newest = history[history.length - 1];
        const windowMs = Math.max(1, newest.t - oldest.t);
        const rawAxisVelocityPerMs = (newest.axisPos - oldest.axisPos) / windowMs;
        const velocity =
          (isInv ? rawAxisVelocityPerMs : -rawAxisVelocityPerMs) * state.dragSensitivity * 1000;

        state.dragBody.velocity = velocity;
        // Once inertia decays to sleep, autoplay resumes in whichever
        // direction the fling was already moving — not back to the
        // configured `direction` attribute.
        if (velocity !== 0) {
          state.autoplaySign = velocity > 0 ? 1 : -1;
        }
        styleTxn.run(() => styleTxn.setProp(state.host, "cursor", "grab"));
      },
      onCancel: () => {
        state.isDragging = false;
        state.dragDisposer = null;
        state.dragBody.velocity = 0;
        // No click follows a cancelled gesture; leaving the pressed node behind
        // would let it resolve a later, unrelated click.
        state.pointerDownTarget = null;
        styleTxn.run(() => styleTxn.setProp(state.host, "cursor", "grab"));
      },
    });

    state.dragDisposer = dispose;
  };
}
